import type { ContractSummaryStatus, SupplierFamilySummary } from "@/types/supplier";

export type ContractSummaryBadgeVariant =
  | "traffic-green"
  | "traffic-amber"
  | "review-neutral"
  | "traffic-red"
  | "outline";

export interface ContractSummaryBadge {
  label: string;
  variant: ContractSummaryBadgeVariant;
}

/**
 * The contract summary status pill, shown on the folder page and in the Supplier Centre family tab.
 * The four spec states are No Summary Created, Unverified, Verified and Needs Updating; the other
 * states keep their earlier wording.
 */
const CONTRACT_SUMMARY_STATUS_LABELS: Record<ContractSummaryStatus, ContractSummaryBadge> = {
  none: { label: "No Summary Created", variant: "outline" },
  current: { label: "Unverified", variant: "traffic-amber" },
  verified: { label: "Verified", variant: "traffic-green" },
  stale: { label: "Needs Updating", variant: "traffic-amber" },
  in_progress: { label: "Generating", variant: "review-neutral" },
  needs_review: { label: "Review needed", variant: "traffic-red" },
  failed: { label: "Failed", variant: "traffic-red" },
  empty: { label: "Folder empty", variant: "traffic-amber" },
};

/** Pill copy for a folder's contract summary state; an unknown or missing status reads as none. */
export const contractSummaryStatusBadge = (status: ContractSummaryStatus | undefined): ContractSummaryBadge =>
  CONTRACT_SUMMARY_STATUS_LABELS[status ?? "none"];

export type ContractSummaryAction = "create" | "update";

export const CONTRACT_SUMMARY_ACTION_LABELS: Record<ContractSummaryAction, string> = {
  create: "Create Contract Summary",
  update: "Update Contract Summary",
};

/**
 * A family whose summary was ever published (up to date, verified, amended, frozen, or failed after a
 * publication) is updated; a family without one is created.
 */
export const contractSummaryAction = (
  family: Pick<SupplierFamilySummary, "contract_summary_published_at"> | undefined,
): ContractSummaryAction => (family?.contract_summary_published_at ? "update" : "create");
